/**
 * Modal de saisie vocale combinée pour SADAStock.
 *
 * L'agent dit une seule phrase comme :
 *   « quantité deux péremption zéro quatre 2030 »
 * → l'app remplit automatiquement les 2 champs.
 *
 * Props :
 * - line: { product_name, ean, ... }  (produit ciblé, affiché en haut)
 * - onApply({ quantity, expiration })  → callback quand l'agent valide
 * - onClose()
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Mic, MicOff, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { parseVoiceCommand, isSpeechAvailable } from '../../../utils/voiceCommand';

const VoiceSaisieDialog = ({ line, onApply, onClose }) => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [parsed, setParsed] = useState({ quantity: null, expiration: null });
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const supported = isSpeechAvailable();

  const start = () => {
    setError(null);
    setTranscript('');
    setParsed({ quantity: null, expiration: null });
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("La reconnaissance vocale n'est pas disponible sur ce navigateur. Utilisez Chrome ou Safari récent.");
      return;
    }
    const rec = new SR();
    rec.lang = 'fr-FR';
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      const full = (final + ' ' + interim).trim();
      setTranscript(full);
      setParsed(parseVoiceCommand(full));
    };
    rec.onerror = (e) => {
      setError(e.error === 'not-allowed'
        ? 'Microphone refusé. Autorisez l\'accès au micro dans les réglages du navigateur.'
        : `Erreur micro : ${e.error || 'inconnue'}`);
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
    };
    try {
      rec.start();
      setListening(true);
      recRef.current = rec;
    } catch (err) {
      setError(err?.message || 'Impossible de démarrer le micro');
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (_) { /* ignore */ }
    setListening(false);
  };

  useEffect(() => {
    // Auto-démarre l'écoute à l'ouverture (UX : l'agent vient juste d'appuyer)
    if (supported) start();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => {
    onApply({ quantity: parsed.quantity, expiration: parsed.expiration });
  };

  const canApply = parsed.quantity !== null || parsed.expiration !== null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+96px)] sm:pb-3"
      onClick={onClose}
      data-testid="voice-saisie-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-md w-full max-h-[calc(100dvh-120px)] sm:max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#5B3B8C] to-[#7C4FB8] px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5" />
            <h2 className="font-semibold">Saisie vocale</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="voice-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3">
          {line && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5">
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Produit ciblé</p>
              <p className="text-sm font-semibold text-zinc-900">{line.product_name}</p>
              {line.ean && <p className="text-xs text-zinc-500 font-mono">EAN {line.ean}</p>}
            </div>
          )}

          {!supported && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 p-2.5 rounded text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Votre navigateur ne supporte pas la reconnaissance vocale. Sur Android : utilisez Chrome récent. Sur iOS : Safari 14.1+.</span>
            </div>
          )}

          {/* Bouton micro central animé */}
          <div className="flex flex-col items-center gap-2 py-3">
            <button
              type="button"
              onClick={listening ? stop : start}
              disabled={!supported}
              data-testid="voice-mic-button"
              className={`w-20 h-20 rounded-full flex items-center justify-center transition shadow-lg ${
                listening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-[#5B3B8C] text-white hover:bg-[#4A2F73]'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {listening ? <MicOff className="w-9 h-9" /> : <Mic className="w-9 h-9" />}
            </button>
            <p className="text-xs text-zinc-500">
              {listening ? '🎤 En écoute… parlez maintenant' : 'Appuyez pour parler'}
            </p>
          </div>

          {/* Transcript en cours */}
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5 min-h-[60px]">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium mb-1">
              Ce que l'app a entendu
            </p>
            {transcript ? (
              <p className="text-sm text-zinc-900 italic">« {transcript} »</p>
            ) : listening ? (
              <p className="text-sm text-zinc-400 italic flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> En attente…
              </p>
            ) : (
              <p className="text-sm text-zinc-400">—</p>
            )}
          </div>

          {/* Aperçu interprétation */}
          <div className="grid grid-cols-2 gap-2">
            <div className={`rounded-md border p-2.5 ${
              parsed.quantity !== null ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Quantité</p>
              <p className="text-2xl font-bold text-zinc-900">
                {parsed.quantity !== null ? parsed.quantity : '—'}
              </p>
            </div>
            <div className={`rounded-md border p-2.5 ${
              parsed.expiration ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'
            }`}>
              <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">Péremption</p>
              <p className="text-lg font-bold text-zinc-900 font-mono">
                {parsed.expiration || '—'}
              </p>
            </div>
          </div>

          <details className="text-xs text-zinc-500">
            <summary className="cursor-pointer">💡 Exemples de phrases</summary>
            <ul className="mt-1 ml-4 list-disc space-y-0.5">
              <li>« quantité deux péremption zéro quatre 2030 »</li>
              <li>« stock cinq péremption avril 2030 »</li>
              <li>« quantité douze »</li>
              <li>« péremption 06/2029 »</li>
            </ul>
          </details>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="shrink-0 sticky bottom-0 border-t border-zinc-200 bg-white px-4 py-3 flex gap-2">
          <Button variant="outline" onClick={start} disabled={listening} className="flex-1" data-testid="voice-retry">
            <Mic className="w-4 h-4 mr-1" /> Réessayer
          </Button>
          <Button
            onClick={apply}
            disabled={!canApply}
            className="flex-1 bg-[#5B3B8C] hover:bg-[#4A2F73] text-white disabled:opacity-50"
            data-testid="voice-apply"
          >
            <Check className="w-4 h-4 mr-1" /> Appliquer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoiceSaisieDialog;
